import MetaTags from "react-meta-tags";
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Alert, Container, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import { AvForm, AvField } from "availity-reactstrap-validation";
import axios from "../api/axios";

import logo from "../../assets/images/logo-sm.svg";
import CarouselPage from "../AuthenticationInner/CarouselPage";

const RESETPASS_URL = "/forget_password/reset";
const CHECKTOKEN_URL = "/forget_password/check_token";
const MIN_PASSWORD_LENGTH = 8;

/** Human message for any failed request — rate limit, server error, or no network. */
const describeError = (error) => {
  const status = error?.response?.status;
  const serverMsg = error?.response?.data?.message;
  if (status === 429) return serverMsg || "Too many attempts. Please wait a few minutes and try again.";
  if (status >= 500) return "The server had a problem. Please try again in a moment.";
  if (!error?.response) return "Could not reach the server. Check your connection and try again.";
  return serverMsg || "Something went wrong. Please try again.";
};

const Shell = ({ title, children }) => (
  <React.Fragment>
    <MetaTags>
      <title>Rentdigicare | {title}</title>
    </MetaTags>
    <div className="auth-page">
      <Container fluid className="p-0">
        <Row className="g-0">
          <Col lg={4} md={5} className="col-xxl-3">
            <div className="auth-full-page-content d-flex p-sm-5 p-4">
              <div className="w-100">
                <div className="d-flex flex-column h-100">
                  <div className="mb-4 mb-md-5 text-center">
                    <Link to="/login" className="d-block auth-logo">
                      <img src={logo} alt="" height="70" />
                    </Link>
                  </div>
                  <div className="auth-content my-auto">{children}</div>
                  <div className="mt-4 mt-md-5 text-center">
                    <p className="mb-0"> {new Date().getFullYear()} © Rentdigicare</p>
                  </div>
                </div>
              </div>
            </div>
          </Col>
          <CarouselPage />
        </Row>
      </Container>
    </div>
  </React.Fragment>
);

/**
 * /reset_password?tkn=…  — the page a reset-link email lands on.
 * Works for every account type (the server resolves the token to the right account).
 */
const ChangePassword = () => {
  const token = useMemo(() => {
    try {
      return (new URLSearchParams(window.location.search).get("tkn") || "").trim();
    } catch (_) {
      return "";
    }
  }, []);

  // "checking" → "form" | "invalid"
  const [phase, setPhase] = useState(token ? "checking" : "invalid");
  const [checkError, setCheckError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [accountLabel, setAccountLabel] = useState("");

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    (async () => {
      try {
        const { data } = await axios.post(CHECKTOKEN_URL, { resetToken: token });
        if (!active) return;
        if (data?.status === 200) {
          setAccountLabel(data.label || "");
          setPhase("form");
        } else {
          setPhase("invalid");
        }
      } catch (err) {
        if (!active) return;
        // Could not verify (network / rate limit) — still let them try; the reset call will decide.
        setCheckError(describeError(err));
        setPhase("form");
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const handleValidSubmit = async (_e, v) => {
    setError("");
    setSuccess("");
    const pw = String(v.new_password || "");
    if (pw.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (pw !== String(v.confirm_password || "")) {
      setError("Passwords are not matching!");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(RESETPASS_URL, { resetToken: token, password: pw });
      if (data?.status === 200) {
        setSuccess("Password changed successfully. Taking you to the login page…");
        setTimeout(() => window.location.replace("/login"), 1500);
        return; // keep the button disabled while we redirect
      }
      if (data?.status === 401) {
        setPhase("invalid");
        return;
      }
      setError(data?.message || "Something went wrong. Please try again.");
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "checking") {
    return (
      <Shell title="Reset Password">
        <div className="text-center py-4">
          <Spinner color="primary" />
          <p className="text-muted mt-3 mb-0">Checking your reset link…</p>
        </div>
      </Shell>
    );
  }

  if (phase === "invalid") {
    return (
      <Shell title="Reset Password">
        <div className="text-center">
          <h5 className="text-danger">Password reset link is invalid or has expired!</h5>
          <p className="text-muted mt-3">Reset links work for one hour and can be used once.</p>
          <Link to="/forgot-password" className="btn btn-primary mt-2">Request a new link</Link>
          <div className="mt-3">
            <Link to="/login">Back to login</Link>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Reset Password">
      <div className="text-center">
        <h5 className="mb-0">Reset Password</h5>
        {accountLabel ? <p className="text-muted mt-2 mb-0">{accountLabel} account</p> : null}
      </div>

      {checkError ? (
        <Alert color="warning" className="mt-3 py-2" style={{ fontSize: 13 }}>{checkError}</Alert>
      ) : null}

      <AvForm className="custom-form mt-4" onValidSubmit={handleValidSubmit}>
        <div className="mb-3 passShowInput">
          <AvField
            name="new_password"
            label="New Password"
            className="form-control"
            placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
            type={showNew ? "text" : "password"}
            autoComplete="new-password"
            validate={{
              required: { value: true, errorMessage: "Please enter a new password" },
              minLength: { value: MIN_PASSWORD_LENGTH, errorMessage: `Password must be at least ${MIN_PASSWORD_LENGTH} characters` },
            }}
          />
          <div className="">
            <i
              className={showNew ? "bx bx-show" : "bx bxs-low-vision"}
              role="button"
              tabIndex={0}
              aria-label={showNew ? "Hide password" : "Show password"}
              onClick={() => setShowNew((s) => !s)}
            />
          </div>
        </div>
        <div className="mb-3 passShowInput">
          <AvField
            name="confirm_password"
            label="Confirm Password"
            className="form-control"
            placeholder="Re-enter the new password"
            type={showConfirm ? "text" : "password"}
            autoComplete="new-password"
            validate={{ required: { value: true, errorMessage: "Please confirm the new password" } }}
          />
          <div className="">
            <i
              className={showConfirm ? "bx bx-show" : "bx bxs-low-vision"}
              role="button"
              tabIndex={0}
              aria-label={showConfirm ? "Hide password" : "Show password"}
              onClick={() => setShowConfirm((s) => !s)}
            />
          </div>
        </div>

        {error ? <h6 className="text-danger">{error}</h6> : null}
        {success ? <h6 className="text-success">{success}</h6> : null}

        <div className="mb-3 mt-4">
          <button className="btn btn-primary w-100 waves-effect waves-light" type="submit" disabled={submitting || !!success}>
            {submitting ? (
              <>
                Resetting <Spinner size="sm" style={{ marginLeft: 6 }} />
              </>
            ) : (
              "Reset"
            )}
          </button>
        </div>
      </AvForm>
    </Shell>
  );
};

export default ChangePassword;
